import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { ACHIEVEMENT_DEFINITIONS, AchievementDefinition, getTotalPossiblePoints } from './achievementDefinitions';
import { AchievementCard } from './AchievementCard';
import { AchievementProgressCard } from './AchievementProgressCard';
import { AchievementDetailModal } from './AchievementDetailModal';
import { AdminAchievementsManager } from './AdminAchievementsManager';
import { useCustomAchievements } from '@/hooks/useCustomAchievements';

interface SellerStats {
  totalVentas: number;
  ventasMes: number;
  totalCanjes: number;
  totalAhorros: number;
  cursosCompletados: number;
  totalCursos: number;
  ranking: number | null;
}

interface UnlockedAchievement {
  type: string;
  unlockedAt: string;
}

const CATEGORY_INFO = {
  ventas: { icon: '🛒', name: 'Ventas', color: 'from-blue-500/20 to-cyan-500/20' },
  canjes: { icon: '🎁', name: 'Canjes', color: 'from-pink-500/20 to-purple-500/20' },
  ahorros: { icon: '💰', name: 'Ahorros', color: 'from-green-500/20 to-emerald-500/20' },
  entrenamiento: { icon: '🎓', name: 'Entrenamiento', color: 'from-indigo-500/20 to-blue-500/20' },
  ranking: { icon: '🏆', name: 'Ranking', color: 'from-amber-500/20 to-yellow-500/20' },
  especial: { icon: '✨', name: 'Especiales', color: 'from-purple-500/20 to-pink-500/20' }
};

export const AchievementsScreen: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const { activeCustomAchievements } = useCustomAchievements();
  const [unlockedAchievements, setUnlockedAchievements] = useState<UnlockedAchievement[]>([]);
  const [stats, setStats] = useState<SellerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedAchievement, setSelectedAchievement] = useState<AchievementDefinition | null>(null);

  useEffect(() => {
    if (user && !isAdmin) {
      fetchData();
    }
  }, [user, isAdmin]);

  // Admin sees the management view
  if (isAdmin) {
    return <AdminAchievementsManager />;
  }

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);

    try {
      // Fetch unlocked achievements from seller_achievements
      const { data: achievementsData } = await supabase
        .from('seller_achievements')
        .select('achievement_type, unlocked_at')
        .eq('seller_id', user.id);

      const unlocked: UnlockedAchievement[] = (achievementsData || []).map(a => ({
        type: a.achievement_type,
        unlockedAt: a.unlocked_at
      }));

      // Fetch cycle milestones from seller_cycle_stats
      const currentCycle = new Date().toISOString().slice(0, 7); // YYYY-MM
      const { data: cycleStats } = await supabase
        .from('seller_cycle_stats')
        .select('*')
        .eq('seller_id', user.id)
        .eq('cycle_month_year', currentCycle)
        .maybeSingle();

      // Add cycle milestones to unlocked list if achieved
      if (cycleStats) {
        const now = new Date().toISOString();
        if (cycleStats.first_sale_achieved && !unlocked.some(u => u.type === 'first_sale')) {
          unlocked.push({ type: 'first_sale', unlockedAt: cycleStats.updated_at || now });
        }
        if (cycleStats.first_redemption_achieved && !unlocked.some(u => u.type === 'first_redemption')) {
          unlocked.push({ type: 'first_redemption', unlockedAt: cycleStats.updated_at || now });
        }
        if (cycleStats.strong_start_achieved && !unlocked.some(u => u.type === 'strong_start')) {
          unlocked.push({ type: 'strong_start', unlockedAt: cycleStats.updated_at || now });
        }
        if (cycleStats.consistency_achieved && !unlocked.some(u => u.type === 'consistency')) {
          unlocked.push({ type: 'consistency', unlockedAt: cycleStats.updated_at || now });
        }
        // Volume milestones
        if ((cycleStats.bronce_sales || 0) >= 10 && !unlocked.some(u => u.type === 'volume_bronce_10')) {
          unlocked.push({ type: 'volume_bronce_10', unlockedAt: cycleStats.updated_at || now });
        }
        if ((cycleStats.plata_sales || 0) >= 10 && !unlocked.some(u => u.type === 'volume_plata_10')) {
          unlocked.push({ type: 'volume_plata_10', unlockedAt: cycleStats.updated_at || now });
        }
        if ((cycleStats.oro_sales || 0) >= 10 && !unlocked.some(u => u.type === 'volume_oro_10')) {
          unlocked.push({ type: 'volume_oro_10', unlockedAt: cycleStats.updated_at || now });
        }
        // Streak milestone
        if ((cycleStats.consecutive_sales_count || 0) >= 3 && !unlocked.some(u => u.type === 'sales_streak_3')) {
          unlocked.push({ type: 'sales_streak_3', unlockedAt: cycleStats.updated_at || now });
        }
      }

      setUnlockedAchievements(unlocked);

      // Fetch seller stats for progress calculation
      const { data: rankingData } = await (supabase as any).rpc('get_seller_ranking_stats');
      if (rankingData && Array.isArray(rankingData)) {
        const myStats = rankingData.find((s: any) => s.seller_id === user.id);
        const myRank = rankingData.findIndex((s: any) => s.seller_id === user.id);
        
        if (myStats) {
          setStats({
            totalVentas: Number(myStats.total_ventas) || 0,
            ventasMes: Number(myStats.total_ventas) || 0, // Current month stats
            totalCanjes: Number(myStats.total_canjes) || 0,
            totalAhorros: Number(myStats.total_ahorros) || 0,
            cursosCompletados: Number(myStats.cursos_completados) || 0,
            totalCursos: Number(myStats.total_cursos) || 0,
            ranking: myRank >= 0 ? myRank + 1 : null
          });
        }
      }
    } catch (err) {
      console.error('Error fetching achievements data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getProgress = (type: string): { current: number; target: number } | undefined => {
    if (!stats) return undefined;

    switch (type) {
      case 'first_sale':
        return { current: stats.totalVentas, target: 1 };
      case 'sales_10':
        return { current: stats.ventasMes, target: 10 };
      case 'sales_50':
        return { current: stats.totalVentas, target: 50 };
      case 'sales_100':
        return { current: stats.totalVentas, target: 100 };
      case 'redemptions_50':
        return { current: stats.totalCanjes, target: 50 };
      case 'savings_100k':
        return { current: stats.totalAhorros, target: 100000 };
      case 'savings_500k':
        return { current: stats.totalAhorros, target: 500000 };
      case 'training_complete':
        return { current: stats.cursosCompletados, target: Math.max(stats.totalCursos, 1) };
      default:
        return undefined;
    }
  };

  const isUnlocked = (type: string): boolean => {
    return unlockedAchievements.some(a => a.type === type);
  };

  const getUnlockedAt = (type: string): string | undefined => {
    return unlockedAchievements.find(a => a.type === type)?.unlockedAt;
  };

  // Combine system and custom achievements
  const allAchievements: AchievementDefinition[] = [
    ...ACHIEVEMENT_DEFINITIONS,
    ...activeCustomAchievements.map(c => ({
      type: c.type,
      badge: c.badge,
      name: c.name,
      description: c.description,
      requirement: c.requirement,
      points: c.points,
      category: c.category as AchievementDefinition['category'],
      isPermanent: c.is_permanent,
    })),
  ];

  const getNextAchievement = (): { achievement: AchievementDefinition; progress: { current: number; target: number } } | null => {
    for (const achievement of allAchievements) {
      if (!isUnlocked(achievement.type)) {
        const progress = getProgress(achievement.type);
        if (progress && progress.current < progress.target) {
          return { achievement, progress };
        }
      }
    }
    return null;
  };

  const totalUnlocked = unlockedAchievements.length;
  const totalAchievements = allAchievements.length;
  const progressPercent = (totalUnlocked / totalAchievements) * 100;
  const totalPoints = unlockedAchievements.reduce((sum, ua) => {
    const def = allAchievements.find(d => d.type === ua.type);
    return sum + (def?.points || 0);
  }, 0);
  const totalPossible = allAchievements.reduce((sum, a) => sum + a.points, 0);

  const nextAchievement = getNextAchievement();

  // Group achievements by category
  const categories = Object.keys(CATEGORY_INFO) as Array<keyof typeof CATEGORY_INFO>;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with overall progress */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-red-500/10 border border-amber-500/20 rounded-2xl p-5"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Mis Logros</h2>
              <p className="text-white/60 text-sm">{totalUnlocked} de {totalAchievements} desbloqueados</p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1.5 text-amber-400">
              <Sparkles className="w-4 h-4" />
              <span className="font-bold text-lg">{totalPoints}</span>
            </div>
            <p className="text-white/40 text-xs">de {totalPossible} pts</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full h-3 bg-black/30 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="h-full bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-full"
          />
        </div>
        <p className="text-center text-white/50 text-xs mt-2">
          {Math.round(progressPercent)}% completado
        </p>
      </motion.div>

      {/* Next achievement card */}
      {nextAchievement && (
        <AchievementProgressCard
          achievement={nextAchievement.achievement}
          progress={nextAchievement.progress}
        />
      )}

      {/* Achievement categories */}
      {categories.map((category, categoryIndex) => {
        const categoryAchievements = allAchievements.filter(a => a.category === category);
        if (categoryAchievements.length === 0) return null;

        const categoryInfo = CATEGORY_INFO[category];

        return (
          <motion.div
            key={category}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: categoryIndex * 0.1 }}
            className="space-y-3"
          >
            {/* Category header */}
            <div className="flex items-center gap-2">
              <span className="text-lg">{categoryInfo.icon}</span>
              <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wide">
                {categoryInfo.name}
              </h3>
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs text-white/40">
                {categoryAchievements.filter(a => isUnlocked(a.type)).length}/{categoryAchievements.length}
              </span>
            </div>

            {/* Achievement grid */}
            <div className="grid grid-cols-4 gap-2 sm:gap-3">
              {categoryAchievements.map((achievement, index) => {
                const unlocked = isUnlocked(achievement.type);
                const unlockedAt = getUnlockedAt(achievement.type);
                const progress = !unlocked ? getProgress(achievement.type) : undefined;

                return (
                  <motion.div
                    key={achievement.type}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: categoryIndex * 0.1 + index * 0.05 }}
                  >
                    <AchievementCard
                      achievement={achievement}
                      isUnlocked={unlocked}
                      unlockedAt={unlockedAt}
                      progress={progress}
                      onClick={() => setSelectedAchievement(achievement)}
                    />
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        );
      })}

      {/* Detail modal */}
      {selectedAchievement && (
        <AchievementDetailModal
          achievement={selectedAchievement}
          isUnlocked={isUnlocked(selectedAchievement.type)}
          unlockedAt={getUnlockedAt(selectedAchievement.type)}
          progress={!isUnlocked(selectedAchievement.type) ? getProgress(selectedAchievement.type) : undefined}
          onClose={() => setSelectedAchievement(null)}
        />
      )}
    </div>
  );
};

export default AchievementsScreen;
